import { useMemo } from 'react';
import { z } from 'zod/v4';

import useGetMyTaxRates from '@/api/queries/me/useGetMyTaxRates';
import { LINE_ITEM_PRICE_FORM_SCHEMA } from '@/ui/components/addEditItemSidebar/fields/LineItemPriceCard';
import { LINE_ITEM_TAXES_FORM_SCHEMA } from '@/ui/components/addEditItemSidebar/fields/LineItemTotalsCard';
import { LINE_ITEM_REFERENCE_FORM_SCHEMA } from '@/ui/components/addEditItemSidebar/fields/ReferenceField';
import { LINE_ITEM_THUMBNAIL_FORM_SCHEMA } from '@/ui/components/addEditItemSidebar/fields/ThumbnailField';
import { LINE_ITEM_TITLE_DESCRIPTION_FORM_SCHEMA } from '@/ui/components/addEditItemSidebar/fields/TitleDescriptionField';
import {
  buildDefaultValues,
  LineItemEditorData,
} from '@/ui/components/addEditProposalLineItem/defaultValues';
import { MaterialState } from '@/ui/components/buildMaterialsListSidebar/BuildMaterialsListSidebar';

export const FORM_SCHEMA = z
  .object({
    selectionType: z.union([
      z.literal('REQUIRED'),
      z.literal('MULTIPLE_CHOICE'),
      z.literal('OPTIONAL'),
    ]),
    quantity: z.number(),
    discountDescription: z.string().optional(),
    materials: z.array(z.any()),
    canUpdateOriginalLineItem: z.boolean(),
    updateOriginalLineItem: z.boolean(),
  })
  .merge(LINE_ITEM_REFERENCE_FORM_SCHEMA)
  .merge(LINE_ITEM_PRICE_FORM_SCHEMA)
  .merge(LINE_ITEM_TITLE_DESCRIPTION_FORM_SCHEMA)
  .merge(LINE_ITEM_THUMBNAIL_FORM_SCHEMA)
  .merge(LINE_ITEM_TAXES_FORM_SCHEMA);

type MaterialFormItem = Pick<
  MaterialState,
  'id' | 'name' | 'image' | 'suppliedBy' | 'quantity' | 'tags'
>;

export type TFormState = Omit<z.infer<typeof FORM_SCHEMA>, 'materials'> & {
  materials: MaterialFormItem[];
};

export const useDefaultValues = (proposalLineItemData?: LineItemEditorData) => {
  const { defaultTaxRate } = useGetMyTaxRates();

  return useMemo(() => {
    if (!proposalLineItemData) {
      return {
        reference: '',
        description: '',
        taxRateId: defaultTaxRate?.id || '',
        cis: 'no' as const,
        quantity: 1,
        markupTotals: {
          markupType: 'price' as const,
          profitPercentage: 0,
          profitAmount: 0,
          price: 0,
        },
        autoUpdatePrice: true,
        selectionType: 'REQUIRED' as const,
        hasDiscount: false,
        materials: [],
        canUpdateOriginalLineItem: false,
        updateOriginalLineItem: false,
      };
    }

    return buildDefaultValues(proposalLineItemData, defaultTaxRate?.id);
  }, [proposalLineItemData, defaultTaxRate?.id]);
};
